import React, { useEffect, useRef, useState } from "react";
import "../moviedetails/movie.css";
import { useParams } from "react-router-dom";
import { Carousel } from "react-responsive-carousel";
import { Box, IconButton, useTheme } from "@mui/material";
import tmdbConfigs from "../api/tmdb.config";
import { auth, db } from "../../firebase";
import { doc, getDoc, getFirestore, setDoc, updateDoc } from "firebase/firestore";
import { Favorite, FavoriteBorder } from "@mui/icons-material";
import Recommendtv from "../Recommend/recommend_tv";

const TVShow = ({ theme }) => {
    // const currentTheme = useTheme();
    const { id } = useParams();
    const [currentShowDetail, setShowDetail] = useState(null);
    const [videos, setVideos] = useState([]);
    const [cast, setCast] = useState([]);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isFavorite, setIsFavorite] = useState(false);
    const [userEmail, setUserEmail] = useState("");
    const [mediaType, setMediaType] = useState(""); // Define mediaType state
    const [genreList, setGenres] = useState([]);

    const iframeRef = useRef();

    useEffect(() => {
        fetchData();
        window.scrollTo(0, 0);
    }, [id]);

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUserEmail(user.email);
            } else {
                setUserEmail("");
            }
        });
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const checkFavoriteStatus = async () => {
            const userId = auth.currentUser?.uid;
            if (!userId) return;
            const userRef = doc(db, "userFavorite", userId);
            const docSnap = await getDoc(userRef);

            if (docSnap.exists()) {
                const favoritesData = docSnap.data().favorites || {};
                setIsFavorite(!!favoritesData[id]);
            }
        };

        checkFavoriteStatus();
    }, [id]);

    const fetchData = () => {
        fetch(
            `https://api.themoviedb.org/3/tv/${id}?api_key=4e44d9029b1270a757cddc766a1bcb63&language=en-US&append_to_response=videos,credits`
        )
            .then((res) => res.json())
            .then((data) => {
                setShowDetail(data);
                if (data?.videos?.results) {
                    setVideos(data.videos.results);
                }
                if (data?.credits?.cast) {
                    setCast(data.credits.cast);
                }
                // Determine mediaType
                if (data.first_air_date) {
                    setMediaType("tv");
                }
            })
            .catch((error) => console.error("Error fetching TV show details:", error));
    };

    const nextSlide = () => {
        setCurrentSlide((prevSlide) => (prevSlide === videos.length - 1 ? 0 : prevSlide + 1));
    };

    const prevSlide = () => {
        setCurrentSlide((prevSlide) => (prevSlide === 0 ? videos.length - 1 : prevSlide - 1));
    };

    useEffect(() => {
        if (videos.length > 0 && iframeRef.current) {
            const height = (iframeRef.current.offsetWidth * 9) / 16 + "px";
            iframeRef.current.setAttribute("height", height);
        }
    }, [currentSlide, videos]);

    const renderGenres = () =>
        currentShowDetail.genres.map((genre) => (
            <span key={genre.id} className="movie__genre">
                {genre.name}
            </span>
        ));

    const renderCast = () =>
        cast.map((actor) => (
            <div key={actor.id} className="movie__cast">
                <img
                    src={`https://image.tmdb.org/t/p/w185${actor.profile_path}`}
                    alt={actor.name}
                    className="movie__castImage small"
                />
                <div className="movie__castName">{actor.name}</div>
                <div className="movie__castCharacter">{actor.character}</div>
            </div>
        ));

    const handleRemoveButtonClick = async () => {
        try {
            const user = auth.currentUser;
            if (!user) {
                console.error("User not logged in");
                return;
            }
            const userId = user.uid;
            const favoritesRef = doc(getFirestore(), "userFavorite", userId);

            const favoritesDoc = await getDoc(favoritesRef);
            if (favoritesDoc.exists()) {
                const favoritesData = favoritesDoc.data().favorites || {};
                if (favoritesData[id]) {
                    delete favoritesData[id];
                    await updateDoc(favoritesRef, { favorites: favoritesData });
                    setIsFavorite(false); // Update isFavorite state
                } else {
                    console.error("Favorite document does not exist:", id);
                }
            } else {
                console.error("Favorites document does not exist for user:", userId);
            }
        } catch (error) {
            console.error("Error removing favorite from list: ", error);
        }
    };

    const toggleFavorite = async () => {
        try {
            const userId = auth.currentUser?.uid;
            if (!userId) return;

            const userRef = doc(db, "userFavorite", userId);
            const docSnap = await getDoc(userRef);

            if (docSnap.exists()) {
                const favoritesData = docSnap.data().favorites || {};
                let favoriteObject = {};

                if (currentShowDetail) {
                    const gen = currentShowDetail.genres || [];
                    setGenres(gen);
                    favoriteObject = {
                        tvId: id,
                        mediaType: "tv", // Set mediaType to 'tv'
                        posterPath: tmdbConfigs.posterPath(currentShowDetail.poster_path),
                        mediaTitle: currentShowDetail.name,
                        mediaRate: currentShowDetail.vote_average,
                        genres: gen,
                    };
                } else {
                    throw new Error("TV show detail not found");
                }

                if (isFavorite) {
                    delete favoritesData[id];
                } else {
                    favoritesData[id] = favoriteObject;
                }

                await setDoc(userRef, { favorites: favoritesData }, {
                    merge: true
                });
                setIsFavorite(!isFavorite);

                console.log(`Toggled favorite for mediaId: ${id} for user: ${userId}`);
            } else {
                // Create the user document if it doesn't exist
                await setDoc(userRef, { favorites: {} });
                console.log("User document created.");
            }
        } catch (error) {
            console.error("Error toggling favorite:", error);
        }
    };

    const updateViewedHistory = async () => {
        try {
            const historyRef = doc(db, "history", userEmail);
            const docSnap = await getDoc(historyRef);

            let historyData = {};
            if (docSnap.exists()) {
                historyData = docSnap.data();
            } else {
                historyData.tvShows = [];
            }

            const showData = {
                tvId: id,
                mediaTitle: currentShowDetail.name,
                posterPath: currentShowDetail?.poster_path,
                genres: currentShowDetail?.genres.map((genre) => genre.name),
                mediaRate: currentShowDetail.vote_average,
                timestamps: [new Date()],
            };

            // Check if the show already exists in the history
            const existingShowIndex = historyData.tvShows
                ? historyData.tvShows.findIndex((show) => show.tvId === id)
                : -1;

            if (existingShowIndex !== -1) {
                // Append new timestamp to the existing show entry
                if (!historyData.tvShows[existingShowIndex].timestamps) {
                    historyData.tvShows[existingShowIndex].timestamps = [];
                }

                historyData.tvShows[existingShowIndex].timestamps.push(new Date());
            } else {
                // Add the new show entry with timestamp
                if (!historyData.tvShows) {
                    historyData.tvShows = [showData];
                } else {
                    historyData.tvShows.push(showData);
                }
            }

            await setDoc(historyRef, historyData);

            console.log("Viewed history updated successfully.");
        } catch (error) {
            console.error("Error updating viewed history:", error);
        }
    };

    useEffect(() => {
        if (currentShowDetail && userEmail) {
            updateViewedHistory();
        }
    }, [currentShowDetail, userEmail]);

    const responsive = {
        superLargeDesktop: {
            breakpoint: { max: 4000, min: 3000 },
            items: 5
        },
        desktop: {
            breakpoint: { max: 3000, min: 1024 },
            items: 4
        },
        tablet: {
            breakpoint: { max: 1024, min: 464 },
            items: 2
        },
        mobile: {
            breakpoint: { max: 464, min: 0 },
            items: 1
        }
    };
    return (
        <div className={`container ${theme}`}>
            <div className="row">
                <div className="col-md-12">
                    <div className="movie__intro">
                        <img
                            className="movie__backdrop"
                            src={`https://image.tmdb.org/t/p/original${currentShowDetail?.backdrop_path || ""}`}
                            alt="Backdrop"
                        />
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-4">
                    <div className="movie__detail">
                        <div className="movie__detailLeft">
                            <div className="movie__posterBox">
                                <img
                                    className="movie__poster"
                                    src={`https://image.tmdb.org/t/p/original${currentShowDetail?.poster_path || ""}`}
                                    alt="Poster"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-md-8">
                    <div className="movie__detailRight">
                        <div className="movie__detailRightTop">
                            <div className="movie__name" style={{ color: theme === "dark" ? "white" : "black" }}>{currentShowDetail?.name || ""}</div>
                            <div className="movie__tagline" style={{ color: theme === "dark" ? "white" : "black" }}>{currentShowDetail?.tagline || ""}</div>
                            <div className="movie__rating" style={{ color: theme === "dark" ? "white" : "black" }}>
                                {currentShowDetail?.vote_average || ""}{" "}
                                <i className="fas fa-star" />
                                <span className="movie__voteCount">
                                {currentShowDetail ? `(${currentShowDetail.vote_count} votes)` : ""}
                            </span>
                            </div>
                            <div className="movie__runtime" style={{ color: theme === "dark" ? "white" : "black" }}>
                                {currentShowDetail ? `${currentShowDetail.episode_run_time[0]} mins` : ""}
                            </div>
                            <div className="movie__releaseDate" style={{ color: theme === "dark" ? "white" : "black" }}>
                                {currentShowDetail ? `First air date: ${currentShowDetail.first_air_date}` : ""}
                            </div>
                            <div className="movie__genres" style={{ color: theme === "dark" ? "white" : "black" }}>
                                {currentShowDetail?.genres ? renderGenres() : ""}
                            </div>
                            <div className="row">
                                <div className="col-md-12">
                                    <IconButton
                                        onClick={isFavorite ? handleRemoveButtonClick : toggleFavorite}
                                        sx={{
                                            "&:hover": { backgroundColor: "transparent" },
                                            color: theme === "dark" ? (isFavorite ? "red" : "lightblue") : (isFavorite ? "red" : "black")
                                        }}
                                    >
                                        {isFavorite ? <Favorite /> : <FavoriteBorder />}
                                    </IconButton>
                                </div>
                            </div>
                        </div>
                        <div className="movie__detailRightBottom mt-3" style={{ color: theme === "dark" ? "white" : "black" }}> {/* Added inline style for text color */}
                            <div className="synopsisText">Synopsis</div>
                            <div>{currentShowDetail?.overview || ""}</div>
                        </div>
                    </div>
                </div>
            </div>
            <br/>   <br/>   <br/>                         <br/>   <br/>   <br/>
            <div className="row">
                <div className="col-md-12">
                    <div className="movie__heading" style={{ marginLeft: "3%", color: theme === "dark" ? "white" : "black" }}>Production companies</div>
                    <div className="movie__production" style={{ display: "flex", flexWrap: "wrap", gap: "10px", overflow: "hidden" }}>
                        {currentShowDetail?.production_companies &&
                            currentShowDetail.production_companies.map((company) =>
                                company.logo_path ? (
                                    <span className="productionCompanyImage" key={company.id} style={{ display: "flex", alignItems: "center", color: theme === "dark" ? "white" : "black" }}>
                                    <img
                                        className="movie__productionCompany"
                                        src={`https://image.tmdb.org/t/p/original${company.logo_path}`}
                                        alt={company.name}
                                        style={{ maxWidth: "100px", height: "auto", marginRight: "5px" }}
                                    />
                                    <span>{company.name}</span>
                                </span>
                                ) : null
                            )}
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="movie__heading" style={{ marginLeft: "3%", color: theme === "dark" ? "white" : "black" }}>Video Trailers</div>
                    {videos.length > 0 && (
                        <div className="d-flex justify-content-center">
                            <div className="embed-responsive embed-responsive-16by9">
                                <Carousel showArrows={true} showThumbs={false} showStatus={false} infiniteLoop={true}>
                                    {videos.map((video, index) => (
                                        <div key={index}>
                                            <iframe
                                                key={video.key}
                                                src={tmdbConfigs.youtubePath(video.key)}
                                                ref={iframeRef}
                                                className="embed-responsive-item"
                                                title={video.id}
                                                style={{ border: 0, height: '80vh', marginLeft: "3%" }}
                                            ></iframe>
                                        </div>
                                    ))}
                                </Carousel>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="row mt-4">
                <div className="col-12">
                    <div className="movie__heading" style={{ marginLeft: "3%", color: theme === "dark" ? "white" : "black" }}>Cast</div>
                    <Carousel responsive={responsive} infinite={true} autoPlay={false} containerClass="movie__castContainer">
                        {cast.map((actor, index) => (
                            <div key={index} className="movie__castSlide p-2">
                                <img
                                    src={`https://image.tmdb.org/t/p/w185${actor.profile_path}`}
                                    alt={actor.name}
                                    className="movie__castImage small img-fluid"
                                />
                                <div className={`movie__castName ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>{actor.name}</div>
                                <div className={`movie__castCharacter ${theme === 'dark' ? 'text-white' : 'text-dark'}`}>{actor.character}</div>
                            </div>
                        ))}
                    </Carousel>
                </div>
            </div>
            <Recommendtv tvId={id} />
        </div>
    );

};

export default TVShow;
